import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Pager, { usePaged } from '../../components/Pager';
import { iterateRows, normaliseHeader, readXlsx, splitLine, writeXlsx } from '../../reports/engine';
import { readPreview } from '../../components/MasterFileUploader';
import { auditService } from '../../services/auditService';
import { downloadFile, fmtQty, safeFileName, todayStamp, XLSX_TYPE } from '../Reports/ui';
import { PageLoader } from '../../components/Loader';
import './LocationUploader.css';
import LocationBatches from './LocationBatches';

const FIELDS = [
  { key: 'location_code', label: 'Location code', required: true, aliases: ['location', 'location code', 'location barcode', 'loc', 'loc code', 'rack', 'rack code', 'bin', 'bin code', 'location no', 'location number', 'location id', 'rack no', 'rack number', 'rack id', 'bin no', 'shelf', 'shelf no', 'shelf code', 'gondola', 'gondola no', 'gondola code', 'tag', 'tag no'] },
  { key: 'location_name', label: 'Location name', aliases: ['location name', 'name', 'description', 'desc', 'rack name', 'rack desc', 'rack description', 'location desc', 'location description', 'shelf name', 'gondola name'] },
  { key: 'zone', label: 'Zone', aliases: ['zone', 'zone name', 'zone code', 'department', 'dept', 'category', 'division'] },
  { key: 'area', label: 'Area', aliases: ['area', 'area name', 'sub area', 'section', 'floor', 'aisle', 'place', 'location type'] },
  { key: 'gc_qty', label: 'GC Qty', aliases: ['gc qty', 'gc quantity', 'gc', 'global count', 'global count qty', 'gc count', 'qty', 'quantity', 'count'] },
];

/** "1,234" -> 1234; blank -> null; anything else -> NaN (flagged). */
function gcNumber(value) {
  const text = String(value ?? '').replace(/,/g, '').trim();
  if (!text) return null;
  const number = Number(text);
  return Number.isFinite(number) && number >= 0 ? number : Number.NaN;
}


const PREVIEW_ROWS = 15;
const clean = (value) => String(value ?? '').replace(/[\t\r\n]+/g, ' ').trim();

/**
 * Header row = first of the first rows that names the location column, or
 * failing that the first row naming two of the columns.
 */
function findHeader(rows) {
  const codeNames = new Set(FIELDS[0].aliases);
  const anyNames = new Set(FIELDS.flatMap((field) => field.aliases));
  const limit = Math.min(rows.length, PREVIEW_ROWS);
  for (let index = 0; index < limit; index += 1) {
    if (rows[index].some((cell) => codeNames.has(normaliseHeader(cell)))) return index;
  }
  for (let index = 0; index < limit; index += 1) {
    if (rows[index].filter((cell) => anyNames.has(normaliseHeader(cell))).length >= 2) return index;
  }
  return -1;
}

/** Aliases are in order of preference: the first one found wins. */
function guessMapping(headers) {
  const cells = headers.map((cell) => normaliseHeader(cell));
  const mapping = {};
  FIELDS.forEach((field) => {
    let index = -1;
    for (const alias of field.aliases) {
      index = cells.indexOf(alias);
      if (index >= 0) break;
    }
    mapping[field.key] = index >= 0 ? String(index) : '';
  });
  return mapping;
}

/**
 * Text (CSV / tab / semicolon / pipe): the separator is picked from the first
 * 20 lines, so a title line above the header does not confuse it.
 */
function parseText(raw) {
  const text = String(raw || '').replace(/^\ufeff/, '');
  const lines = text.split(/\r?\n/).filter((line) => line.trim()).slice(0, 20);
  let best = '\t';
  let bestScore = 0;
  ['\t', ',', ';', '|'].forEach((delimiter) => {
    const score = lines.reduce((sum, line) => sum + (splitLine(line, delimiter).length > 1 ? 1 : 0), 0);
    if (score > bestScore) {
      best = delimiter;
      bestScore = score;
    }
  });
  return [...iterateRows(text, best)].map((row) => ({
    line: row.lineNumber,
    cells: row.cells.map((cell) => String(cell ?? '')),
  }));
}

/** { name, rows, lines }: lines[i] = the row's number in the file (as Excel shows it). */
function sheetOf(name, numbered) {
  const kept = numbered.filter((row) => row.cells.some((cell) => clean(cell)));
  return { name, rows: kept.map((row) => row.cells), lines: kept.map((row) => row.line) };
}

/** All sheets of the file as rows of text (first sheet for text files). */
async function readSheets(file) {
  if (/\.xls$/i.test(file.name)) {
    throw new Error('Old .xls files are not supported. Save the file as .xlsx or CSV and try again.');
  }
  if (!/\.(xlsx|xlsb)$/i.test(file.name)) {
    return [sheetOf(file.name, parseText(await file.text()))];
  }
  if (/\.xlsx$/i.test(file.name)) {
    const sheets = await readXlsx(new Uint8Array(await file.arrayBuffer()));
    return sheets.map((sheet) =>
      sheetOf(
        sheet.name,
        sheet.rows.map((row, index) => ({ line: row.rowNumber || index + 1, cells: row.cells.map((cell) => String(cell ?? '')) })),
      ),
    );
  }
  const preview = await readPreview(file);
  return Promise.all(
    preview.sheets.map(async (sheet, index) =>
      sheetOf(
        sheet.name,
        (await preview.readAll(index)).map((row, rowIndex) => ({ line: rowIndex + 1, cells: row.map((cell) => String(cell ?? '')) })),
      ),
    ),
  );
}

// The store team's location sheet: Location Number is the code; Zone and Area
// are two separate groupings (e.g. zone "Apparel", area "Floor").
// Date, Site Code, GC Qty and the users are kept in the file for their own
// use; the upload reads only the columns chosen below.
const TEMPLATE = [
  ['Date', 'Site Code', 'Location Number', 'Zone', 'Area', 'GC Qty'],
  ['02-11-2026', 'PT001', 'SIA2026F00001', 'Apparel', 'Floor', '22'],
  ['02-11-2026', 'PT001', 'SIA2026F00002', 'Apparel', 'Floor', '12'],
  ['02-11-2026', 'PT001', 'SIA2026F00005', 'Front end', 'Cash till', '12'],
  ['02-11-2026', 'PT001', 'SIA2026F00008', 'Admin', 'Office', '45'],
  ['02-11-2026', 'PT001', 'SIA2026B00010', 'Apparel', 'Backstock', '66'],
];

function downloadTemplate() {
  const cell = (value) => (/[",\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value);
  const text = `\ufeff${TEMPLATE.map((row) => row.map(cell).join(',')).join('\r\n')}\r\n`;
  downloadFile('location-master-template.csv', text, 'text/csv;charset=utf-8');
}


// ---- Duplicate location numbers -------------------------------------------
// A location number may appear on several rows (copied down by mistake, or
// one rack counted in parts). For each repeated number the user accepts one
// row (first / last / a chosen row), accepts every row as separate locations
// (renumbered CODE-2, CODE-3 …) or rejects them all.

const codeKey = (code) => String(code || '').trim().toUpperCase();

/** [{ key, code, rows, differs }] for location numbers on more than one row. */
function duplicateGroups(parsed) {
  const byKey = new Map();
  parsed.forEach((row) => {
    const key = codeKey(row.location_code);
    if (!byKey.has(key)) byKey.set(key, []);
    byKey.get(key).push(row);
  });
  return [...byKey.entries()]
    .filter(([, list]) => list.length > 1)
    .map(([key, list]) => ({
      key,
      code: list[0].location_code,
      rows: list,
      differs: new Set(list.map((r) => `${codeKey(r.location_name)}|${codeKey(r.zone)}|${codeKey(r.area)}`)).size > 1,
      gcTotal: list.some((r) => r.gc_qty !== null) ? list.reduce((t, r) => t + (r.gc_qty || 0), 0) : null,
    }));
}

/**
 * Applies the decisions: { accepted, rejected: [{ ...row, reason }], renamed }.
 * fallback = decision for repeated numbers the user has not set: "merge"
 * (one location, GC quantities added up) when the file has a GC column.
 */
function resolveDuplicates(parsed, groups, decisions, fallback = 'first') {
  const byKey = new Map(groups.map((group) => [group.key, group]));
  const taken = new Set(parsed.map((row) => codeKey(row.location_code)));
  const counters = new Map();
  const accepted = [];
  const rejected = [];
  let renamed = 0;

  parsed.forEach((row) => {
    const group = byKey.get(codeKey(row.location_code));
    if (!group) {
      accepted.push(row);
      return;
    }
    const decision = decisions[group.key] || fallback;
    const first = group.rows[0];
    const last = group.rows[group.rows.length - 1];
    if (decision === 'merge') {
      if (row === first) {
        accepted.push({
          ...row,
          gc_qty: group.gcTotal,
          merged_rows: group.rows.length,
        });
      } else {
        rejected.push({ ...row, reason: `Merged into row ${first.line} (GC Qty added up)` });
      }
    } else if (decision === 'reject') {
      rejected.push({ ...row, reason: 'Repeated location number - all rows rejected' });
    } else if (decision === 'unique') {
      if (row === first) {
        accepted.push(row);
        return;
      }
      let n = counters.get(group.key) || 1;
      let code;
      do {
        n += 1;
        code = `${group.code}-${n}`;
      } while (taken.has(codeKey(code)));
      counters.set(group.key, n);
      taken.add(codeKey(code));
      renamed += 1;
      accepted.push({ ...row, location_code: code, renamed_from: row.location_code });
    } else {
      const keep = decision === 'last' ? last : decision.startsWith('row:') ? group.rows.find((r) => `row:${r.line}` === decision) || first : first;
      if (row === keep) accepted.push(row);
      else rejected.push({ ...row, reason: `Repeated location number - row ${keep.line} accepted` });
    }
  });
  return {
    accepted,
    rejected,
    renamed,
    merged: rejected.filter((row) => row.reason.startsWith('Merged')).length,
  };
}

function outcomeText(group, decision) {
  const n = group.rows.length;
  if (decision === 'merge') {
    return `1 location from ${n} rows${group.gcTotal !== null ? `, GC Qty ${fmtQty(group.gcTotal)}` : ''}`;
  }
  if (decision === 'reject') return `Not uploaded (${n} rows rejected)`;
  if (decision === 'unique') return `${n} locations: ${group.code}, ${group.code}-2 … ${group.code}-${n}`;
  const keep = decision === 'last' ? group.rows[n - 1] : decision.startsWith('row:') ? group.rows.find((r) => `row:${r.line}` === decision) : group.rows[0];
  return `Row ${keep?.line} uploaded, ${n - 1} rejected`;
}

function DuplicateReview({ groups, decisions, fallback, hasGc, onDecide, onDecideAll, rejected, fileName }) {
  const [open, setOpen] = useState(true);
  const extra = groups.reduce((sum, group) => sum + group.rows.length - 1, 0);
  const paged = usePaged(groups, { pageSize: 25, sizes: [25, 50, 100], storageKey: 'loc-dups' });

  const downloadRejected = () => {
    const cell = (value) => (/[",\n]/.test(String(value)) ? `"${String(value).replace(/"/g, '""')}"` : String(value ?? ''));
    const lines = [
      ['Row', 'Location number', 'Location name', 'Zone', 'Area', 'GC Qty', 'Reason'],
      ...rejected.map((r) => [r.line, r.location_code, r.location_name, r.zone, r.area, r.gc_qty ?? '', r.reason]),
    ];
    downloadFile(
      `${String(fileName || 'locations').replace(/\.[^.]+$/, '')} - rejected rows.csv`,
      `\ufeff${lines.map((row) => row.map(cell).join(',')).join('\r\n')}\r\n`,
      'text/csv;charset=utf-8',
    );
  };

  return (
    <section className="loc-dups" aria-label="Repeated location numbers">
      <header className="loc-dups__head">
        <div>
          <strong>
            {fmtQty(groups.length)} location number{groups.length === 1 ? '' : 's'} repeated ({fmtQty(extra)} extra row{extra === 1 ? '' : 's'})
          </strong>
          <p>
            Choose what to do with each one.{' '}
            {hasGc
              ? 'By default the rows are merged into one location and their GC Qty is added up (one location counted in parts).'
              : 'Rejected rows are not uploaded.'}
          </p>
        </div>
        <button type="button" className="master-upload__link" onClick={() => setOpen((value) => !value)}>
          {open ? 'Hide' : 'Review'}
        </button>
      </header>

      <div className="loc-dups__bulk">
        <span>For all:</span>
        {hasGc ? (
          <button type="button" onClick={() => onDecideAll('merge')}>Merge, add up GC Qty</button>
        ) : null}
        <button type="button" onClick={() => onDecideAll('first')}>Accept first row</button>
        <button type="button" onClick={() => onDecideAll('last')}>Accept last row</button>
        <button type="button" onClick={() => onDecideAll('unique')}>Accept all as separate</button>
        <button type="button" className="loc-dups__reject" onClick={() => onDecideAll('reject')}>Reject all</button>
        {rejected.length ? (
          <button type="button" className="loc-dups__download" onClick={downloadRejected}>
            Download rejected rows (CSV)
          </button>
        ) : null}
      </div>

      {open ? (
        <>
        <div className="loc-dups__scroll" ref={paged.anchorRef}>
          <table className="loc-dups__table">
            <thead>
              <tr>
                <th>Location number</th>
                <th>Rows in file</th>
                <th>{hasGc ? 'Zone / area / GC Qty' : 'Name / zone / area'}</th>
                <th>Accept</th>
                <th>Result</th>
              </tr>
            </thead>
            <tbody>
              {paged.items.map((group) => {
                const decision = decisions[group.key] || fallback;
                const details = [...new Set(group.rows.map((r) => [r.location_name, r.zone, r.area].filter(Boolean).join(' · ') || '—'))];
                const gcList = group.rows.filter((r) => r.gc_qty !== null).map((r) => fmtQty(r.gc_qty));
                return (
                  <tr key={group.key} className={decision === 'reject' ? 'loc-dups__row--reject' : ''}>
                    <td>
                      <code>{group.code}</code>
                      <small>{fmtQty(group.rows.length)} times</small>
                    </td>
                    <td className="loc-dups__lines">
                      {group.rows.length <= 8
                        ? group.rows.map((r) => r.line).join(', ')
                        : `${group.rows.slice(0, 6).map((r) => r.line).join(', ')} … ${group.rows[group.rows.length - 1].line}`}
                    </td>
                    <td>
                      {details.slice(0, 3).join(' | ')}
                      {details.length > 3 ? ` (+${details.length - 3})` : ''}
                      {gcList.length ? (
                        <small>
                          GC {gcList.length > 6 ? `${gcList.slice(0, 6).join(' + ')} + …` : gcList.join(' + ')} = <b>{fmtQty(group.gcTotal)}</b>
                        </small>
                      ) : null}
                      {group.differs ? <small className="loc-dups__warn">Details differ between rows</small> : null}
                    </td>
                    <td>
                      <select
                        aria-label={`Decision for ${group.code}`}
                        value={decision}
                        onChange={(event) => onDecide(group.key, event.target.value)}
                      >
                        {hasGc ? <option value="merge">Merge into one location, add up GC Qty</option> : null}
                        <option value="first">First row (row {group.rows[0].line})</option>
                        <option value="last">Last row (row {group.rows[group.rows.length - 1].line})</option>
                        {group.rows.slice(1, -1).map((r) => (
                          <option key={r.line} value={`row:${r.line}`}>
                            Row {r.line}
                            {[r.location_name, r.zone, r.area].filter(Boolean).length ? ` (${[r.location_name, r.zone, r.area].filter(Boolean).join(' · ')})` : ''}
                          </option>
                        ))}
                        <option value="unique">All rows, as separate locations ({group.code}-2 …)</option>
                        <option value="reject">Reject all rows</option>
                      </select>
                    </td>
                    <td className="loc-dups__outcome">{outcomeText(group, decision)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <Pager paged={paged} noun="repeated numbers" compact />
        </>
      ) : null}
    </section>
  );
}

/**
 * Loads a location master (the racks / shelves / gondolas expected in the
 * store), the same way as the master file: Excel (.xlsx / .xlsb), CSV or the
 * phone's .txt format (Location,Location Name,Quantity,User), with or
 * without a header row. The user picks the sheet, the header row and which
 * columns hold the code, name, zone, area and GC; a template is offered.
 */
function LocationUploader({ auditId, onDone, onCancel }) {
  const inputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [sheets, setSheets] = useState([]);
  const [sheetIndex, setSheetIndex] = useState(0);
  const [headerRow, setHeaderRow] = useState(-1); // -1 = no header row
  const [mapping, setMapping] = useState({ location_code: '', location_name: '', zone: '', area: '', gc_qty: '' });
  const [reading, setReading] = useState(false);
  const [progress, setProgress] = useState(null);
  const [error, setError] = useState('');

  const rows = useMemo(() => sheets[sheetIndex]?.rows || [], [sheets, sheetIndex]);
  const lines = useMemo(() => sheets[sheetIndex]?.lines || [], [sheets, sheetIndex]);

  const applyGuess = (list) => {
    const header = findHeader(list);
    setHeaderRow(header);
    // No header (e.g. the phone's location .txt): column 1 = code, 2 = name.
    const width = list.reduce((max, row) => Math.max(max, row.length), 0);
    setMapping(
      header >= 0
        ? guessMapping(list[header])
        : {
            location_code: '0',
            location_name: width > 1 ? '1' : '',
            zone: '',
            area: '',
            gc_qty: width > 2 ? '2' : '',
          },
    );
  };

  const choose = async (event) => {
    const picked = event.target.files?.[0];
    event.target.value = '';
    if (!picked) return;
    setError('');
    setReading(true);
    try {
      const read = await readSheets(picked);
      const first = Math.max(0, read.findIndex((sheet) => sheet.rows.length > 0));
      if (!read[first]?.rows.length) throw new Error('The file has no rows.');
      setFile(picked);
      setSheets(read);
      setSheetIndex(first);
      applyGuess(read[first].rows);
    } catch (err) {
      setError(err.message);
      setFile(null);
      setSheets([]);
    } finally {
      setReading(false);
    }
  };

  const columns = useMemo(() => {
    const width = rows.reduce((max, row) => Math.max(max, row.length), 0);
    const header = headerRow >= 0 ? rows[headerRow] || [] : [];
    return Array.from({ length: width }, (_, index) => {
      const sample = rows.find((row, rowIndex) => rowIndex > headerRow && clean(row[index]));
      return {
        index,
        label: clean(header[index]) || `Column ${index + 1}`,
        sample: sample ? clean(sample[index]).slice(0, 24) : '',
      };
    });
  }, [rows, headerRow]);

  const parsed = useMemo(() => {
    if (!rows.length || mapping.location_code === '' || mapping.location_code === undefined) return [];
    const pick = (row, key) => (mapping[key] === '' ? '' : clean(row[Number(mapping[key])]));
    return rows
      .slice(headerRow + 1)
      .map((row, index) => ({
        line: lines[headerRow + 1 + index] || headerRow + index + 2,
        location_code: pick(row, 'location_code'),
        location_name: pick(row, 'location_name'),
        zone: pick(row, 'zone'),
        area: pick(row, 'area'),
        gc_raw: pick(row, 'gc_qty'),
        gc_qty: gcNumber(pick(row, 'gc_qty')),
      }))
      .filter((row) => row.location_code);
  }, [rows, lines, mapping, headerRow]);

  // ---- Duplicate location numbers: accept / reject per code ------------
  const groups = useMemo(() => duplicateGroups(parsed), [parsed]);
  const [decisions, setDecisions] = useState({});
  useEffect(() => setDecisions({}), [groups]);
  const decide = (key, value) => setDecisions((prev) => ({ ...prev, [key]: value }));
  const decideAll = (value) => setDecisions(Object.fromEntries(groups.map((g) => [g.key, value])));
  const hasGc = Boolean(mapping.gc_qty);
  const fallback = hasGc ? 'merge' : 'first';
  const resolved = useMemo(
    () => resolveDuplicates(parsed, groups, decisions, fallback),
    [parsed, groups, decisions, fallback],
  );
  const badGc = parsed.filter((row) => Number.isNaN(row.gc_qty));
  const gcTotal = resolved.accepted.reduce((t, row) => t + (Number.isFinite(row.gc_qty) ? row.gc_qty : 0), 0);

  const unique = resolved.accepted.length;
  const blankCodes = useMemo(() => {
    if (!rows.length || mapping.location_code === '' || mapping.location_code === undefined) return 0;
    return rows.slice(headerRow + 1).filter((row) => row.some((cell) => clean(cell)) && !clean(row[Number(mapping.location_code)])).length;
  }, [rows, mapping, headerRow]);
  const tooLong = parsed.filter((row) => row.location_code.length > 100).length;

  const upload = async () => {
    setError('');
    setProgress({ part: 0, parts: 1 });
    try {
      const payload = resolved.accepted.map(({ location_code, location_name, zone, area, gc_qty: gc }) => ({
        location_code,
        location_name,
        zone,
        area,
        gc_qty: Number.isFinite(gc) ? gc : null,
      }));
      const result = await auditService.uploadLocations(auditId, { rows: payload, filename: file.name }, setProgress);
      setProgress(null);
      onDone(result);
    } catch (err) {
      setProgress(null);
      setError(`Location master not uploaded: ${err.message}`);
    }
  };

  const busy = reading || Boolean(progress);

  return (
    <div className="master-upload">
      <div className="master-upload__row">
        <button type="button" className="master-upload__choose" onClick={() => inputRef.current?.click()} disabled={busy}>
          {reading ? 'Reading file…' : file ? `File: ${file.name}` : 'Choose location master (.xlsx, .xlsb, .csv, .txt)…'}
        </button>
        <input
          ref={inputRef}
          type="file"
          accept=".xlsx,.xlsb,.csv,.txt,.tsv"
          onChange={choose}
          hidden
          aria-label="Location master file"
        />
        <button type="button" className="master-upload__link" onClick={downloadTemplate} disabled={busy}>
          Download template (CSV)
        </button>
        <button type="button" className="master-upload__link" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
      </div>
      {!file ? (
        <p className="master-upload__hint">
          Template columns: Date, Site Code, <b>Location Number</b> (required), Zone, Area, GC Qty. Other
          layouts work too: you choose the columns after picking the file. The phone's
          location .txt (no header) also works.
        </p>
      ) : null}
      {error ? <p className="master-upload__error">{error}</p> : null}

      {rows.length ? (
        <>
          <div className="master-upload__grid">
            {sheets.length > 1 ? (
              <label>
                Sheet
                <select
                  value={sheetIndex}
                  onChange={(event) => {
                    const index = Number(event.target.value);
                    setSheetIndex(index);
                    applyGuess(sheets[index].rows);
                  }}
                  disabled={busy}
                >
                  {sheets.map((sheet, index) => (
                    <option key={sheet.name} value={index}>
                      {sheet.name}
                    </option>
                  ))}
                </select>
              </label>
            ) : null}
            <label>
              Header row
              <select
                value={headerRow}
                onChange={(event) => {
                  const index = Number(event.target.value);
                  setHeaderRow(index);
                  if (index >= 0) setMapping(guessMapping(rows[index] || []));
                }}
                disabled={busy}
              >
                <option value={-1}>No header row (data starts at row 1)</option>
                {rows.slice(0, PREVIEW_ROWS).map((row, index) => (
                  <option key={index} value={index}>
                    Row {lines[index] || index + 1}: {row.slice(0, 4).map((cell) => clean(cell) || '—').join(' | ')}
                  </option>
                ))}
              </select>
            </label>
            {FIELDS.map((field) => (
              <label key={field.key}>
                {field.label}
                {field.required ? ' *' : ''}
                <select
                  aria-label={`${field.label} column`}
                  value={mapping[field.key] ?? ''}
                  onChange={(event) => setMapping((prev) => ({ ...prev, [field.key]: event.target.value }))}
                  disabled={busy}
                >
                  <option value="">{field.required ? 'Choose column…' : 'Not in this file'}</option>
                  {columns.map((column) => (
                    <option key={column.index} value={String(column.index)}>
                      {column.index + 1}: {column.label}
                      {column.sample ? ` (e.g. ${column.sample})` : ''}
                    </option>
                  ))}
                </select>
              </label>
            ))}
          </div>

          {mapping.location_code === '' ? (
            <p className="master-upload__hint">Choose the column that holds the location code.</p>
          ) : (
            <>
              <p className="master-upload__hint">
                {fmtQty(parsed.length)} rows → <b>{fmtQty(unique)} locations to upload</b>
                {resolved.merged ? ` · ${fmtQty(resolved.merged)} rows merged` : ''}
                {resolved.rejected.length - resolved.merged ? ` · ${fmtQty(resolved.rejected.length - resolved.merged)} rows rejected` : ''}
                {resolved.renamed ? ` · ${fmtQty(resolved.renamed)} renumbered` : ''}
                {hasGc ? ` · GC Qty total ${fmtQty(gcTotal)}` : ''}
                {badGc.length ? ` · ${fmtQty(badGc.length)} GC Qty values are not numbers (e.g. row ${badGc[0].line}: "${badGc[0].gc_raw}") and are left blank` : ''}
                {blankCodes ? ` · ${fmtQty(blankCodes)} rows without a location number skipped` : ''}
                {tooLong ? ` · ${fmtQty(tooLong)} codes longer than 100 characters (check the column)` : ''}
              </p>
              {groups.length ? (
                <DuplicateReview
                  groups={groups}
                  decisions={decisions}
                  onDecide={decide}
                  onDecideAll={decideAll}
                  fallback={fallback}
                  hasGc={hasGc}
                  rejected={resolved.rejected}
                  fileName={file?.name}
                />
              ) : null}
              <div className="master-upload__preview">
                <table>
                  <thead>
                    <tr>
                      <th>Location code</th>
                      <th>Name</th>
                      <th>Zone</th>
                      <th>Area</th>
                      {hasGc ? <th>GC Qty</th> : null}
                    </tr>
                  </thead>
                  <tbody>
                    {resolved.accepted.slice(0, 5).map((row, index) => (
                      <tr key={index}>
                        <td>
                          {row.location_code}
                          {row.renamed_from ? <small className="loc-dups__renamed"> (was {row.renamed_from})</small> : null}
                        </td>
                        <td>{row.location_name || '—'}</td>
                        <td>{row.zone || '—'}</td>
                        <td>{row.area || '—'}</td>
                        {hasGc ? (
                          <td>
                            {Number.isFinite(row.gc_qty) ? fmtQty(row.gc_qty) : '—'}
                            {row.merged_rows ? <small className="loc-dups__renamed"> ({row.merged_rows} rows)</small> : null}
                          </td>
                        ) : null}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          <div className="master-upload__row">
            <button type="button" className="master-upload__submit" onClick={upload} disabled={!parsed.length || busy}>
              {progress ? `Uploading part ${Math.min(progress.part + 1, progress.parts)} of ${progress.parts}…` : `Upload ${fmtQty(unique)} locations`}
            </button>
            <span className="master-upload__hint">Replaces this schedule's location master. Phones get it with “Sync AMD from Server”.</span>
          </div>
        </>
      ) : null}
    </div>
  );
}

function when(iso) {
  if (!iso) return '—';
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

const LOCATION_FILTERS = [
  { id: 'all', label: 'All locations' },
  { id: 'pending', label: 'Not scanned yet' },
  { id: 'scanned', label: 'Scanned' },
  { id: 'recount', label: 'Recount requested' },
  { id: 'gc_mismatch', label: 'GC: not matching' },
  { id: 'gc_short', label: 'GC: short (scanned < GC)' },
  { id: 'gc_excess', label: 'GC: excess (scanned > GC)' },
  { id: 'gc_match', label: 'GC: matched' },
];

const GC_LABEL = { match: 'GC matched', short: 'Short of GC', excess: 'Over GC', pending: 'Not scanned yet', no_gc: '' };
const signed = (value) => `${value > 0 ? '+' : ''}${fmtQty(value)}`;

/**
 * Zones & locations tab: which racks / shelves have been scanned, by zone,
 * plus locations scanned that are not in the master.
 */
function LocationsPanel({ auditId, auditName, canManage, refreshKey, onRequestRecount, onLocationsLoaded }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [uploading, setUploading] = useState(false);
  const [filter, setFilter] = useState('all');
  const [zone, setZone] = useState('');
  const [area, setArea] = useState('');
  const [groupBy, setGroupBy] = useState('');
  const [search, setSearch] = useState('');
  const [notice, setNotice] = useState('');

  const load = useCallback(async () => {
    try {
      const result = await auditService.getLocations(auditId);
      setData(result);
      setError('');
      onLocationsLoaded?.(result.locations.map((row) => row.location_code));
    } catch (err) {
      setError(err.message);
    }
  }, [auditId, onLocationsLoaded]);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  const shown = useMemo(() => {
    if (!data) return [];
    const term = search.trim().toLowerCase();
    return data.locations.filter((row) => {
      if (zone && (row.zone || '') !== zone) return false;
      if (area && (row.area || '') !== area) return false;
      if (filter === 'pending' && row.status !== 'pending') return false;
      if (filter === 'scanned' && row.status !== 'scanned') return false;
      if (filter === 'recount' && !row.open_recounts) return false;
      if (filter === 'gc_mismatch' && !['short', 'excess'].includes(row.gc_status)) return false;
      if (filter.startsWith('gc_') && filter !== 'gc_mismatch' && row.gc_status !== filter.slice(3)) return false;
      if (!term) return true;
      return `${row.location_code} ${row.location_name || ''} ${row.zone || ''} ${row.area || ''} ${row.people || ''}`
        .toLowerCase()
        .includes(term);
    });
  }, [data, filter, zone, area, search]);

  // Paging (1000s of locations): tiles, zone / area cards, "not in master".
  const tilesPaged = usePaged(shown, {
    pageSize: 60,
    sizes: [30, 60, 120, 240],
    resetKey: `${filter}|${zone}|${area}|${search}`,
    storageKey: 'portal-locations',
  });
  const groupKeyNow = groupBy || (data?.zones?.length ? 'zone' : 'area');
  const groupItems = useMemo(
    () => (data ? (groupKeyNow === 'zone' ? data.zones : data.areas || []) : []),
    [data, groupKeyNow],
  );
  const zonesPaged = usePaged(groupItems, { pageSize: 24, sizes: [12, 24, 48, 96], resetKey: groupKeyNow, storageKey: 'portal-zones' });
  const unexpectedList = useMemo(() => data?.unexpected || [], [data]);
  const unexpectedPaged = usePaged(unexpectedList, { pageSize: 25, sizes: [25, 50, 100], storageKey: 'portal-unexpected' });

  const removeMaster = async () => {
    // eslint-disable-next-line no-alert
    if (!window.confirm('Remove this schedule\'s location master? Scans are not affected.')) return;
    try {
      await auditService.deleteLocations(auditId);
      await load();
    } catch (err) {
      setError(err.message);
    }
  };

  const exportExcel = async () => {
    const columns = [
      { label: 'Location', key: 'location_code', type: 'text', width: 18 },
      { label: 'Name', key: 'location_name', type: 'text', width: 24 },
      { label: 'Zone', key: 'zone', type: 'text', width: 16 },
      { label: 'Area', key: 'area', type: 'text', width: 16 },
      { label: 'Status', key: 'status', type: 'text', value: (r) => (r.status === 'scanned' ? 'Scanned' : r.status === 'pending' ? 'Not scanned' : 'Not in master') },
      { label: 'GC Qty', key: 'gc_qty', type: 'qty' },
      { label: 'Scanned Qty (all)', key: 'counted_qty', type: 'qty' },
      { label: 'Scanned - GC', key: 'gc_diff', type: 'qty' },
      { label: 'GC Check', key: 'gc_status', type: 'text', value: (r) => GC_LABEL[r.gc_status] || '' },
      { label: 'Valid Qty', key: 'valid_qty', type: 'qty' },
      { label: 'Invalid Qty', key: 'invalid_qty', type: 'qty' },
      { label: 'Scans', key: 'scans', type: 'number' },
      { label: 'EANs', key: 'eans', type: 'number' },
      { label: 'Scanned By', key: 'people', type: 'text', width: 28 },
      { label: 'First Scan', key: 'first_scan_at', type: 'text', width: 20, value: (r) => (r.first_scan_at ? new Date(r.first_scan_at).toLocaleString() : '') },
      { label: 'Last Scan', key: 'last_scan_at', type: 'text', width: 20, value: (r) => (r.last_scan_at ? new Date(r.last_scan_at).toLocaleString() : '') },
      { label: 'Open Recounts', key: 'open_recounts', type: 'number' },
    ];
    const bytes = await writeXlsx([
      {
        name: 'Locations',
        title: [`${auditName} — Location progress`],
        notes: [
          `${data.summary.scanned_locations} of ${data.summary.total_locations} locations scanned. Generated ${new Date().toLocaleString()}.`,
          ...(data.summary.gc_locations
            ? [
                `GC check: ${data.summary.gc_match} of ${data.summary.gc_locations} locations match their global count (GC Qty ${data.summary.gc_qty}, scanned ${data.summary.gc_counted_qty}). Scanned = valid + invalid scans at the location.`,
              ]
            : []),
        ],
        columns,
        rows: data.locations,
      },
      {
        name: 'Not in master',
        title: [`${auditName} — Locations scanned that are not in the location master`],
        columns,
        rows: data.unexpected.map((row) => ({ ...row, status: 'unexpected' })),
      },
    ]);
    downloadFile(`${safeFileName(auditName)} - locations ${todayStamp()}.xlsx`, bytes, XLSX_TYPE);
  };

  if (error && !data) return <section className="audit-portal__panel live-panel"><p className="compare-error">{error}</p></section>;
  if (!data) return <section className="audit-portal__panel live-panel"><PageLoader label="Loading locations…" /></section>;

  const { summary, meta } = data;
  // Group tiles by zone, or by area when there are no zones (or chosen).
  const areas = data.areas || [];
  const groupKey = groupBy || (data.zones.length ? 'zone' : 'area');
  const groupList = groupKey === 'zone' ? data.zones : areas;
  const hasMaster = summary.total_locations > 0;

  return (
    <section className="audit-portal__panel live-panel">
      <header className="live-panel__header">
        <div>
          <h2>Zones &amp; locations</h2>
          <p className="live-muted">
            {meta
              ? `Location master: ${meta.original_filename || 'uploaded'} · ${fmtQty(meta.location_count)} locations${meta.zone_count ? ` · ${meta.zone_count} zones` : ''}${meta.area_count ? ` · ${meta.area_count} areas` : ''}`
              : 'No location master yet. Upload the list of racks / shelves to track which have been scanned.'}
          </p>
        </div>
        <div className="live-panel__actions">
          {canManage && !uploading ? (
            <button type="button" className="compare-button" onClick={() => setUploading(true)}>
              {hasMaster ? 'Replace location master' : 'Upload location master'}
            </button>
          ) : null}
          {canManage && hasMaster && !uploading ? (
            <button type="button" className="compare-link" onClick={removeMaster}>
              Remove
            </button>
          ) : null}
          {hasMaster || data.unexpected.length ? (
            <button type="button" className="compare-button compare-button--ghost" onClick={exportExcel}>
              Download Excel
            </button>
          ) : null}
        </div>
      </header>

      {uploading ? (
        <LocationUploader
          auditId={auditId}
          onCancel={() => setUploading(false)}
          onDone={(result) => {
            setUploading(false);
            setNotice(`Location master uploaded: ${fmtQty(result.row_count)} locations${result.duplicates_removed ? ` (${fmtQty(result.duplicates_removed)} repeated codes skipped)` : ''}.`);
            load();
          }}
        />
      ) : null}
      {notice ? <p className="live-notice">{notice}</p> : null}
      {error ? <p className="compare-error">{error}</p> : null}

      <section className="compare-cards">
        <article className="compare-card compare-card--good">
          <span>Locations completed</span>
          <strong>{hasMaster ? `${fmtQty(summary.scanned_locations)} / ${fmtQty(summary.total_locations)}` : '—'}</strong>
          {hasMaster ? (
            <div className="live-bar" aria-label={`${summary.percent}% done`}>
              <div style={{ width: `${summary.percent || 0}%` }} />
            </div>
          ) : (
            <p>Needs a location master</p>
          )}
        </article>
        <article className="compare-card">
          <span>Not scanned yet</span>
          <strong>{hasMaster ? fmtQty(summary.pending_locations) : '—'}</strong>
          <p>Locations in the master with no scans</p>
        </article>
        {summary.gc_locations ? (
          <article className={`compare-card${summary.gc_short + summary.gc_excess ? ' compare-card--warn' : ' compare-card--good'}`}>
            <span>GC check</span>
            <strong>
              {fmtQty(summary.gc_match)} / {fmtQty(summary.gc_locations)}
            </strong>
            <p>
              {summary.gc_short + summary.gc_excess ? (
                <button type="button" className="compare-link" onClick={() => setFilter('gc_mismatch')}>
                  {fmtQty(summary.gc_short)} short · {fmtQty(summary.gc_excess)} over GC
                </button>
              ) : summary.gc_pending ? (
                'No mismatch so far'
              ) : (
                'All locations match their GC'
              )}
              {summary.gc_pending ? ` · ${fmtQty(summary.gc_pending)} not scanned yet` : ''}
              <br />
              GC {fmtQty(summary.gc_qty)} · scanned {fmtQty(summary.gc_counted_qty)} ({signed(summary.gc_counted_qty - summary.gc_qty)})
            </p>
          </article>
        ) : null}
        <article className={`compare-card${summary.unexpected_locations ? ' compare-card--bad' : ''}`}>
          <span>Not in master</span>
          <strong>{fmtQty(summary.unexpected_locations)}</strong>
          <p>Scanned at a location that is not in the list</p>
        </article>
      </section>

      {summary.scans_without_location ? (
        <p className="live-muted">
          {fmtQty(summary.scans_without_location)} scans have no location (sent by an older app
          version) and are not counted here.
        </p>
      ) : null}

      {hasMaster ? <LocationBatches auditId={auditId} canManage={canManage} refreshKey={refreshKey} /> : null}

      {groupList.length ? (
        <>
          {data.zones.length && (data.areas || []).length ? (
            <div className="live-groupby" role="group" aria-label="Group locations by">
              <span>Group by</span>
              <button type="button" aria-pressed={groupKey === 'zone'} onClick={() => setGroupBy('zone')}>
                Zone
              </button>
              <button type="button" aria-pressed={groupKey === 'area'} onClick={() => setGroupBy('area')}>
                Area
              </button>
            </div>
          ) : null}
          <div className="live-zones" ref={zonesPaged.anchorRef}>
            {zonesPaged.items.map((item) => {
              const name = item[groupKey];
              const selected = groupKey === 'zone' ? zone : area;
              const select = groupKey === 'zone' ? setZone : setArea;
              const pct = item.total ? Math.round((item.scanned / item.total) * 100) : 0;
              return (
                <button
                  key={name}
                  type="button"
                  className={`live-zone${selected === name ? ' live-zone--active' : ''}`}
                  onClick={() => select(selected === name ? '' : name)}
                  aria-pressed={selected === name}
                >
                  <strong>{name}</strong>
                  <span>
                    {item.scanned} / {item.total} · {pct}%
                    {summary.gc_locations ? ` · GC ${fmtQty(item.gc_match)} ok` : ''}
                  </span>
                  <div className="live-bar live-bar--thin">
                    <div style={{ width: `${pct}%` }} />
                  </div>
                </button>
              );
            })}
          </div>
          <Pager paged={zonesPaged} noun={groupKey === 'zone' ? 'zones' : 'areas'} compact />
        </>
      ) : null}

      {hasMaster ? (
        <>
          <div className="compare-toolbar">
            <select value={filter} onChange={(event) => setFilter(event.target.value)} aria-label="Show">
              {LOCATION_FILTERS.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.label}
                </option>
              ))}
            </select>
            <input
              type="search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search location or person…"
              aria-label="Search locations"
            />
            {zone ? (
              <button type="button" className="compare-link" onClick={() => setZone('')}>
                Zone: {zone} ×
              </button>
            ) : null}
            {area ? (
              <button type="button" className="compare-link" onClick={() => setArea('')}>
                Area: {area} ×
              </button>
            ) : null}
            <span className="compare-muted">{fmtQty(shown.length)} shown</span>
          </div>
          <div className="live-tiles" ref={tilesPaged.anchorRef}>
            {tilesPaged.items.map((row) => (
              <article key={row.location_code} className={`live-tile live-tile--${row.status}`}>
                <header>
                  <strong title={row.location_name || ''}>{row.location_code}</strong>
                  {row.open_recounts ? <span className="live-pill live-pill--warn">Recount</span> : null}
                </header>
                {row.location_name || row.zone || row.area ? (
                  <small>{[row.location_name, row.zone, row.area].filter(Boolean).join(' · ')}</small>
                ) : null}
                {row.gc_status !== 'no_gc' ? (
                  <p className={`live-gc live-gc--${row.gc_status}`}>
                    {row.gc_status === 'pending' ? (
                      <>GC {fmtQty(row.gc_qty)}</>
                    ) : (
                      <>
                        GC {fmtQty(row.gc_qty)} · scanned {fmtQty(row.counted_qty)}
                        {row.gc_diff ? ` (${signed(row.gc_diff)})` : ' ✓'}
                      </>
                    )}
                  </p>
                ) : null}
                {row.status === 'scanned' ? (
                  <p>
                    Qty {fmtQty(row.valid_qty)}
                    {row.invalid_qty ? <span className="live-bad"> · {fmtQty(row.invalid_qty)} invalid</span> : null}
                    <br />
                    <span className="live-muted">
                      {row.people || '—'} · last {when(row.last_scan_at)}
                    </span>
                  </p>
                ) : (
                  <p className="live-muted">Not scanned yet</p>
                )}
                <button type="button" className="compare-link" onClick={() => onRequestRecount({ location_code: row.location_code })}>
                  Request recount
                </button>
              </article>
            ))}
          </div>
          {!shown.length ? <p className="compare-empty">No locations match.</p> : null}
          <Pager paged={tilesPaged} noun="locations" />
        </>
      ) : null}

      {data.unexpected.length ? (
        <>
          <h3 className="live-subhead">Scanned at locations not in the master ({fmtQty(data.unexpected.length)})</h3>
          <div className="compare-table-scroll compare-table-scroll--short" ref={unexpectedPaged.anchorRef}>
            <table className="compare-table">
              <thead>
                <tr>
                  <th>Location</th>
                  <th className="num">Valid qty</th>
                  <th className="num">Invalid qty</th>
                  <th>Scanned by</th>
                  <th>Last scan</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {unexpectedPaged.items.map((row) => (
                  <tr key={row.location_code}>
                    <td>{row.location_code}</td>
                    <td className="num">{fmtQty(row.valid_qty)}</td>
                    <td className="num">{fmtQty(row.invalid_qty)}</td>
                    <td>{row.people || '—'}</td>
                    <td>{when(row.last_scan_at)}</td>
                    <td>
                      <button type="button" className="compare-link" onClick={() => onRequestRecount({ location_code: row.location_code })}>
                        Request recount
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pager paged={unexpectedPaged} noun="locations" compact />
        </>
      ) : null}
    </section>
  );
}

export { LocationUploader };
export default LocationsPanel;
